import {InvoiceItem,PaymentRecord,RecurringFrequency,InvoiceStatus} from '../types';

// Canonical currency rounding helper. All invoice monetary amounts are normalised
// to cents before they are persisted or rolled up so line totals and invoice totals
// cannot disagree because of JavaScript floating-point representation.
export const roundMoney=(value: number): number=> {
const rounded=Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
return Object.is(rounded, -0) || Math.abs(rounded) < 0.005 ? 0 : rounded;
};

export interface InvoiceItemFinancials {
subtotal: number;
discount: number;
netSubtotal: number;
taxAmount: number;
total: number;
}

export const calculateInvoiceItemFinancials=(item: InvoiceItem): InvoiceItemFinancials=> {
const quantity=Number(item.quantity || 0);
const unitPrice=Number(item.unitPrice || 0);
const subtotal=roundMoney(quantity * unitPrice);
const discount=roundMoney(Math.max(0,Math.min(Number(item.discount || 0),subtotal)));
const netSubtotal=roundMoney(subtotal - discount);
const taxable=item.taxTreatment==='gst_10' || item.taxTreatment==='custom';
const taxAmount=taxable ? roundMoney(netSubtotal * Number(item.taxRate || 0)) : 0;
const total=roundMoney(netSubtotal + taxAmount);
return {subtotal,discount,netSubtotal,taxAmount,total};
};

export const recalculateInvoiceItem=(item: InvoiceItem): InvoiceItem=> {
const financials=calculateInvoiceItemFinancials(item);
return {
...item,
quantity: Number(item.quantity || 0),
unitPrice: Number(item.unitPrice || 0),
discount: financials.discount,
subtotal: financials.subtotal,
taxAmount: financials.taxAmount,
total: financials.total
};
};

export const calculateItemTotal=(item: InvoiceItem): number=> {
return calculateInvoiceItemFinancials(item).total;
};

export const calculateSubtotal=(items: InvoiceItem[]): number=> {
return roundMoney(items.reduce((sum,item)=> sum + calculateInvoiceItemFinancials(item).netSubtotal,0));
};

export const calculateTax=(_subtotal: number,_gstRate: number=0.10,items: InvoiceItem[]): number=> {
return roundMoney(items.reduce((sum,item)=> sum + calculateInvoiceItemFinancials(item).taxAmount,0));
};

export const calculateTotal=(subtotal: number,taxAmount: number): number=> {
return roundMoney(subtotal + taxAmount);
};

export const calculatePaymentTotals=(payments: PaymentRecord[])=> {
let totalPaid=0;
let totalRefunded=0;
let totalAdjustments=0;
payments.forEach(p=> {
const amount=Number(p.amount || 0);
if (['deposit','part_payment','final_payment'].includes(p.type)) {
totalPaid +=amount;
} else if (p.type==='refund') {
totalRefunded +=amount;
} else if (p.type==='adjustment') {
totalAdjustments +=amount;
}
});
return {
totalPaid: roundMoney(totalPaid),
totalRefunded: roundMoney(totalRefunded),
totalAdjustments: roundMoney(totalAdjustments)
};
};

export const calculateBalanceDueWithPayments=(
invoiceTotal: number,
totalPaid: number,
totalRefunded: number,
totalAdjustments: number
): number=> {
return roundMoney(
Number(invoiceTotal || 0)
- Number(totalPaid || 0)
+ Number(totalRefunded || 0)
- Number(totalAdjustments || 0)
);
};

export const calculateBalanceDue=(total: number,amountPaid: number): number=> {
return roundMoney(Number(total || 0) - Number(amountPaid || 0));
};

export const calculateNextInvoiceDate=(currentDate: string,frequency: RecurringFrequency): string=> {
const date=new Date(currentDate);
switch (frequency) {
case 'weekly': date.setDate(date.getDate() + 7);break;
case 'monthly': date.setMonth(date.getMonth() + 1);break;
case 'annual': date.setFullYear(date.getFullYear() + 1);break;
}
return date.toISOString();
};

export const generateCycleKey=(profileId: string,servicePeriodStart: string,servicePeriodEnd: string): string=> {
return `${profileId}_${servicePeriodStart}_${servicePeriodEnd}`;
};

export const calculateServicePeriod=(startDate: string,frequency: RecurringFrequency): {start: string;end: string}=> {
const start=new Date(startDate);
const end=new Date(start);
switch (frequency) {
case 'weekly': end.setDate(end.getDate() + 6);break;
case 'monthly': end.setMonth(end.getMonth() + 1);end.setDate(end.getDate() - 1);break;
case 'annual': end.setFullYear(end.getFullYear() + 1);end.setDate(end.getDate() - 1);break;
}
return {start: start.toISOString(),end: end.toISOString()};
};

export const calculateDueDate=(issueDate: string,dueDateRule: 'immediate' | 'net_14' | 'net_30'): string=> {
const date=new Date(issueDate);
switch (dueDateRule) {
case 'immediate': return date.toISOString();
case 'net_14': date.setDate(date.getDate() + 14);return date.toISOString();
case 'net_30': date.setDate(date.getDate() + 30);return date.toISOString();
default: date.setDate(date.getDate() + 14);return date.toISOString();
}
};

export const validateCalculations=(items: InvoiceItem[]): boolean=> {
return items.every(item=> {
const calculated=calculateItemTotal(item);
return Math.abs(calculated - item.total) < 0.01;
});
};

export const calculateOverdueStatus=(invoice: any,payments: PaymentRecord[]): InvoiceStatus=> {
if (['paid','overpaid','cancelled','void','overdue'].includes(invoice.status)) {
return invoice.status;
}
const now=new Date();
const dueDate=new Date(invoice.dueDate);
const {totalPaid,totalRefunded,totalAdjustments}=calculatePaymentTotals(payments.filter(p=> p.invoiceId===invoice.id));
const balanceDue=calculateBalanceDueWithPayments(invoice.total,totalPaid,totalRefunded,totalAdjustments);
if (balanceDue <=0) {
return balanceDue===0 ? 'paid' : 'overpaid';
}
if (dueDate < now) {
return 'overdue';
}
return invoice.status;
};